import { collection, doc, getDocs, limit, query, serverTimestamp, updateDoc, where, writeBatch } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import { trimCompletions } from '@/logic/recurrence'
import type { DateStr, Settings, Task } from '@/types'
import { taskCollection, updateTask, type Paths } from './tasks'

/** Writes one or more settings fields on users/{uid}. */
export async function updateSettings(uid: string, patch: Partial<Settings>): Promise<void> {
  const data: Record<string, unknown> = { updatedAt: serverTimestamp() }
  for (const [k, v] of Object.entries(patch)) data[`settings.${k}`] = v
  await updateDoc(doc(db, 'users', uid), data)
}

/**
 * Settings' clean-up, run only when asked: deletes one-offs ticked before a
 * day and trims the ticks a repeat or routine kept from before it. History is
 * otherwise kept for good, so past weeks stay complete. Returns what went.
 */
export async function cleanUpBefore(p: Paths, before: DateStr, open: Task[]): Promise<{ tasks: number; ticks: number }> {
  let tasks = 0
  for (const scope of ['shared', 'private'] as const) {
    for (;;) {
      const snap = await getDocs(query(taskCollection(p, scope), where('done', '==', true), where('doneOn', '<', before), limit(300)))
      if (snap.empty) break
      const batch = writeBatch(db)
      snap.forEach((d) => batch.delete(d.ref))
      await batch.commit()
      tasks += snap.size
      if (snap.size < 300) break
    }
  }
  let ticks = 0
  for (const t of open) {
    const kept = trimCompletions(t.completions ?? [], before)
    if (kept.length === (t.completions ?? []).length) continue
    ticks += (t.completions ?? []).length - kept.length
    await updateTask(p, t, { completions: kept })
  }
  return { tasks, ticks }
}

export { collection }
